import React, { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { IoAddOutline, IoTrashOutline, IoPencilOutline, IoShieldCheckmark, IoClose, IoSaveOutline, IoEyeOutline, IoPeopleOutline, IoSearchOutline } from 'react-icons/io5';
import { fetchRoles, createRole, updateRole, deleteRole, getUsersByRole, fetchAllUsers, assignRoleToUser, revokeRoleFromUser } from '../../services/api';
import { PERMISSIONS_TREE } from '../../constants/permissions';

const RoleModal = ({ isOpen, onClose, roleToEdit, mode, onSave, userIdToken }) => {
    const isViewMode = mode === 'view';
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [selectedPermissions, setSelectedPermissions] = useState(new Set());
    const [activeTab, setActiveTab] = useState('permissions'); // 'permissions' or 'users'
    const [assignedUsers, setAssignedUsers] = useState([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);

    // For assigning new users
    const [allUsersSearch, setAllUsersSearch] = useState('');
    const [queriedUsers, setQueriedUsers] = useState([]);
    const [isSearchingUsers, setIsSearchingUsers] = useState(false);

    // Original save state
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setActiveTab('permissions');
            if (roleToEdit) {
                setName(roleToEdit.name || '');
                setDescription(roleToEdit.description || '');
                setSelectedPermissions(new Set(roleToEdit.permissions || []));
                loadAssignedUsers(roleToEdit.id || roleToEdit._id);
            } else {
                setName('');
                setDescription('');
                setSelectedPermissions(new Set());
                setAssignedUsers([]);
            }
            // Reset search when modal opens
            setAllUsersSearch('');
            setQueriedUsers([]);
        }
    }, [isOpen, roleToEdit]);

    const loadAssignedUsers = async (roleId) => {
        setIsLoadingUsers(true);
        try {
            const res = await getUsersByRole(userIdToken, roleId);
            setAssignedUsers(res.data || []);
        } catch (error) {
            toast.error(`Failed to load assigned users: ${error.message}`);
        } finally {
            setIsLoadingUsers(false);
        }
    };

    const loadQueriedUsers = async (search = '') => {
        setIsSearchingUsers(true);
        try {
            const res = await fetchAllUsers(userIdToken, { search, limit: 50 });
            setQueriedUsers(res.users || []);
        } catch (error) {
            console.error('Failed to load users', error);
        } finally {
            setIsSearchingUsers(false);
        }
    };

    // Load queried users when switching to the users tab or searching
    useEffect(() => {
        if (activeTab === 'users' && roleToEdit) {
            const delayDebounceFn = setTimeout(() => {
                loadQueriedUsers(allUsersSearch);
            }, 300);
            return () => clearTimeout(delayDebounceFn);
        }
    }, [allUsersSearch, activeTab, roleToEdit]);

    const displayUsers = useMemo(() => {
        const assignedIds = new Set(assignedUsers.map(u => u.user_id));

        if (!allUsersSearch) {
            // Show assigned users first, then unassigned
            const formattedAssigned = assignedUsers.map(a => ({
                id: a.user_id,
                name: a.name || "Unknown",
                email: a.email || "Unknown"
            }));
            const unassignedFromQuery = queriedUsers.filter(u => !assignedIds.has(u.id));
            return [...formattedAssigned, ...unassignedFromQuery];
        } else {
            return queriedUsers;
        }
    }, [allUsersSearch, assignedUsers, queriedUsers]);

    const handleAssignUser = async (userId) => {
        if (!roleToEdit) return;
        try {
            await assignRoleToUser(userIdToken, userId, roleToEdit.id || roleToEdit._id);
            toast.success("User assigned successfully");
            loadAssignedUsers(roleToEdit.id || roleToEdit._id);
            // No need to reload available since displayUsers updates dynamically
        } catch (error) {
            toast.error(`Failed to assign user: ${error.message}`);
        }
    };

    const handleRevokeUser = async (userId) => {
        if (!roleToEdit) return;
        if (window.confirm("Are you sure you want to remove this user from the role?")) {
            try {
                await revokeRoleFromUser(userIdToken, userId, roleToEdit.id || roleToEdit._id);
                toast.success("User removed from role");
                loadAssignedUsers(roleToEdit.id || roleToEdit._id);
            } catch (error) {
                toast.error(`Failed to remove user: ${error.message}`);
            }
        }
    };

    if (!isOpen) return null;

    const handleTogglePermission = (reqPermId) => {
        if (isViewMode) return;
        const newSet = new Set(selectedPermissions);
        if (newSet.has(reqPermId)) {
            newSet.delete(reqPermId);
        } else {
            newSet.add(reqPermId);
        }
        setSelectedPermissions(newSet);
    };

    const handleToggleCategory = (categoryPermissions) => {
        if (isViewMode) return;
        const newSet = new Set(selectedPermissions);
        const allSelected = categoryPermissions.every(p => selectedPermissions.has(p.id));

        categoryPermissions.forEach(p => {
            if (allSelected) {
                newSet.delete(p.id);
            } else {
                newSet.add(p.id);
            }
        });
        setSelectedPermissions(newSet);
    };

    const handleSave = async () => {
        if (!name.trim()) {
            toast.error("Role name is required");
            return;
        }

        setIsSaving(true);
        try {
            const roleData = {
                name: name.trim(),
                description: description.trim(),
                permissions: Array.from(selectedPermissions)
            };

            if (roleToEdit) {
                await updateRole(userIdToken, roleToEdit.id || roleToEdit._id, roleData);
                toast.success("Role updated successfully");
            } else {
                await createRole(userIdToken, roleData);
                toast.success("Role created successfully");
            }
            onSave();
            onClose();
        } catch (error) {
            toast.error(`Error saving role: ${error.message || 'Unknown error'}`);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4 animate-fade-in">
            <div className="bg-1 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col border border-line animate-fade-in-up">

                {/* Modal Header */}
                <div className="flex items-center justify-between p-5 border-b border-line bg-2/30 rounded-t-xl">
                    <h3 className="text-xl font-bold text-p-text flex items-center gap-2">
                        <IoShieldCheckmark className="text-[#5652CF]" />
                        {isViewMode ? 'View Role' : roleToEdit ? 'Edit Role' : 'Create New Role'}
                    </h3>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-2 transition-colors">
                        <IoClose className="h-6 w-6 text-s-text" />
                    </button>
                </div>

                {/* Modal Body */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">

                    {/* Basic Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block mb-2 text-sm font-semibold text-p-text uppercase tracking-wider">Role Name</label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                disabled={isViewMode}
                                placeholder="e.g. Content Manager"
                                className="w-full bg-2 border border-line rounded-lg px-4 py-2.5 text-p-text focus:outline-none focus:ring-2 focus:ring-[#5652CF] transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-1"
                            />
                        </div>
                        <div>
                            <label className="block mb-2 text-sm font-semibold text-p-text uppercase tracking-wider">Description</label>
                            <input
                                type="text"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                disabled={isViewMode}
                                placeholder="Brief summary of this role..."
                                className="w-full bg-2 border border-line rounded-lg px-4 py-2.5 text-p-text focus:outline-none focus:ring-2 focus:ring-[#5652CF] transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-1"
                            />
                        </div>
                    </div>

                    {/* Tabs */}
                    {roleToEdit && (
                        <div className="flex border-b border-line mb-6">
                            <button
                                className={`px-4 py-2 font-semibold text-sm transition-colors border-b-2 ${activeTab === 'permissions' ? 'border-[#5652CF] text-[#5652CF]' : 'border-transparent text-s-text hover:text-p-text'}`}
                                onClick={() => setActiveTab('permissions')}
                            >
                                <span className="flex items-center gap-2"><IoShieldCheckmark /> Permissions</span>
                            </button>
                            <button
                                className={`px-4 py-2 font-semibold text-sm transition-colors border-b-2 ${activeTab === 'users' ? 'border-[#5652CF] text-[#5652CF]' : 'border-transparent text-s-text hover:text-p-text'}`}
                                onClick={() => setActiveTab('users')}
                            >
                                <span className="flex items-center gap-2">
                                    <IoPeopleOutline /> Assigned Users
                                    <span className="bg-2/80 text-xs px-2 py-0.5 rounded-full">{assignedUsers.length}</span>
                                </span>
                            </button>
                        </div>
                    )}

                    {/* Permissions Tab Content */}
                    <div className={activeTab === 'permissions' ? 'block' : 'hidden'}>
                        <div className="flex items-center justify-between mb-4">
                            <label className="block text-sm font-semibold text-p-text uppercase tracking-wider">Permissions</label>
                            <span className="text-xs bg-[#5652CF]/10 text-blue-400 px-2 py-1 rounded-md font-medium">
                                {selectedPermissions.size} Selected
                            </span>
                        </div>

                        <div className="space-y-4">
                            {PERMISSIONS_TREE.map((category, idx) => {
                                const categoryPermissions = category.permissions;
                                const isAllSelected = categoryPermissions.length > 0 && categoryPermissions.every(p => selectedPermissions.has(p.id));
                                const isSomeSelected = categoryPermissions.some(p => selectedPermissions.has(p.id)) && !isAllSelected;

                                return (
                                    <div key={idx} className="bg-2/40 border border-line rounded-lg overflow-hidden transition-all hover:bg-2/50">
                                        <div
                                            className={`px-4 py-3 bg-2/80 border-b border-line flex items-center justify-between transition-colors ${isViewMode ? 'cursor-default' : 'cursor-pointer hover:bg-2'}`}
                                            onClick={() => handleToggleCategory(categoryPermissions)}
                                        >
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="checkbox"
                                                    checked={isAllSelected}
                                                    readOnly={isViewMode}
                                                    ref={input => {
                                                        if (input) input.indeterminate = isSomeSelected;
                                                    }}
                                                    onClick={(e) => { if (isViewMode) e.preventDefault(); }}
                                                    onChange={() => { }} // Handled by div onClick
                                                    className={`w-4 h-4 text-[#5652CF] bg-1 border-line rounded focus:ring-[#5652CF] focus:ring-2 ${isViewMode ? 'cursor-default pointer-events-none opacity-100' : ''}`}
                                                />
                                                <span className="font-bold text-p-text">{category.category}</span>
                                            </div>
                                            <span className="text-xs text-s-text font-medium">
                                                {categoryPermissions.filter(p => selectedPermissions.has(p.id)).length} / {categoryPermissions.length}
                                            </span>
                                        </div>

                                        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                                            {categoryPermissions.map(perm => (
                                                <label
                                                    key={perm.id}
                                                    className={`flex items-start gap-3 p-3 rounded-lg transition-colors border border-transparent ${isViewMode ? 'cursor-default' : 'hover:bg-1 cursor-pointer hover:border-line group'}`}
                                                >
                                                    <div className="pt-0.5">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedPermissions.has(perm.id)}
                                                            readOnly={isViewMode}
                                                            onClick={(e) => { if (isViewMode) e.preventDefault(); }}
                                                            onChange={() => handleTogglePermission(perm.id)}
                                                            className={`w-4 h-4 text-[#5652CF] bg-1 border-line rounded focus:ring-[#5652CF] focus:ring-2 ${isViewMode ? 'cursor-default pointer-events-none opacity-100' : ''}`}
                                                        />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-semibold text-p-text group-hover:text-blue-400 transition-colors">{perm.label}</p>
                                                        <p className="text-xs text-s-text mt-0.5">{perm.description}</p>
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Users Tab Content */}
                    {roleToEdit && (
                        <div className={activeTab === 'users' ? 'block space-y-4' : 'hidden'}>
                            <div className="bg-2/30 p-4 rounded-xl border border-line">
                                <h4 className="text-sm font-bold text-p-text mb-3">Manage Users for {roleToEdit.name}</h4>
                                <div className="flex items-center bg-1 border border-line rounded-lg px-3 py-2 text-p-text focus-within:ring-2 focus-within:ring-[#5652CF] transition-all mb-4">
                                    <IoSearchOutline className="text-s-text mr-2 text-lg" />
                                    <input
                                        type="text"
                                        className="bg-transparent w-full focus:outline-none text-sm"
                                        placeholder="Search users by name or email to assign..."
                                        value={allUsersSearch}
                                        onChange={(e) => setAllUsersSearch(e.target.value)}
                                    />
                                    {isSearchingUsers && <div className="w-4 h-4 border-2 border-[#5652CF] border-t-transparent rounded-full animate-spin ml-2"></div>}
                                </div>

                                <div className="border border-line rounded-xl overflow-hidden bg-1">
                                    <ul className="divide-y divide-line max-h-96 overflow-y-auto custom-scrollbar">
                                        {isLoadingUsers || isSearchingUsers ? (
                                            <li className="p-4 text-center text-sm text-s-text">
                                                <div className="inline-block w-5 h-5 border-2 border-[#5652CF] border-t-transparent rounded-full animate-spin mr-2"></div>
                                                Loading users...
                                            </li>
                                        ) : displayUsers.length === 0 ? (
                                            <li className="p-4 text-center text-sm text-s-text">No users found.</li>
                                        ) : (
                                            displayUsers.map(user => {
                                                const isAssigned = assignedUsers.some(a => a.user_id === user.id);
                                                return (
                                                    <li key={user.id} className="flex items-center justify-between p-3 hover:bg-2/50 transition-colors">
                                                        <label className="flex items-center gap-3 w-full cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={isAssigned}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) handleAssignUser(user.id);
                                                                    else handleRevokeUser(user.id);
                                                                }}
                                                                className={`w-4 h-4 text-[#5652CF] bg-1 border-line rounded focus:ring-[#5652CF] focus:ring-2`}
                                                            />
                                                            <div>
                                                                <p className="text-sm font-semibold text-p-text">{user.name}</p>
                                                                <p className="text-xs text-s-text">{user.email}</p>
                                                            </div>
                                                        </label>
                                                    </li>
                                                );
                                            })
                                        )}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Modal Footer */}
                <div className="flex flex-col-reverse sm:flex-row items-center justify-between p-5 border-t border-line bg-2/30 rounded-b-xl gap-3">
                    <p className="text-xs text-s-text text-center sm:text-left">
                        {roleToEdit && roleToEdit.is_system_role && !isViewMode && 'Note: System roles cannot be completely deleted.'}
                    </p>
                    <div className="flex gap-3 w-full sm:w-auto">
                        <button
                            onClick={onClose}
                            className={`flex-1 sm:flex-none px-5 py-2.5 text-sm font-semibold rounded-lg transition-all ${isViewMode ? 'text-white bg-blue-600 hover:bg-[#5652CF] shadow-lg shadow-blue-500/20' : 'text-s-text bg-transparent border border-line hover:bg-2 hover:text-p-text'}`}
                        >
                            {isViewMode ? 'Close' : 'Cancel'}
                        </button>
                        {!isViewMode && (
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-bold text-white bg-blue-600 rounded-lg hover:bg-[#5652CF] disabled:opacity-50 disabled:cursor-wait shadow-lg shadow-blue-500/20 transition-all hover:-translate-y-0.5"
                            >
                                <IoSaveOutline className="text-lg" />
                                {isSaving ? 'Saving...' : 'Save Role'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export const RolesPermissionsPanel = ({ userIdToken }) => {
    const [roles, setRoles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [roleToEdit, setRoleToEdit] = useState(null);
    const [modalMode, setModalMode] = useState('create');

    const loadRoles = async () => {
        setIsLoading(true);
        try {
            const res = await fetchRoles(userIdToken);
            setRoles(res.data || []);
        } catch (error) {
            toast.error(`Failed to load roles: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (userIdToken) {
            loadRoles();
        }
    }, [userIdToken]);

    const handleCreateNew = () => {
        setRoleToEdit(null);
        setModalMode('create');
        setIsModalOpen(true);
    };

    const handleEdit = (role, e) => {
        if (e) e.stopPropagation();
        setRoleToEdit(role);
        setModalMode('edit');
        setIsModalOpen(true);
    };

    const handleView = (role) => {
        setRoleToEdit(role);
        setModalMode('view');
        setIsModalOpen(true);
    };

    const handleDelete = async (role, e) => {
        if (e) e.stopPropagation();
        if (role.is_system_role) {
            toast.error("Cannot delete system roles.");
            return;
        }

        if (window.confirm(`Are you sure you want to delete the role "${role.name}"?`)) {
            try {
                await deleteRole(userIdToken, role.id || role._id);
                toast.success("Role deleted successfully");
                loadRoles();
            } catch (error) {
                toast.error(`Failed to delete role: ${error.message}`);
            }
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 bg-1 border border-line rounded-xl shadow-lg">
                <div>
                    <h2 className="text-xl font-bold flex items-center gap-2 text-p-text">
                        <IoShieldCheckmark className="text-[#5652CF]" />
                        Roles & Permissions
                    </h2>
                    <p className="text-sm text-s-text mt-1">Manage custom roles and configure access limits.</p>
                </div>
                <button
                    onClick={handleCreateNew}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-[#5652CF] text-white rounded-lg text-sm font-semibold shadow-md transition-all hover:-translate-y-0.5"
                >
                    <IoAddOutline className="text-lg" /> Create Role
                </button>
            </div>

            <div className="bg-1 border border-line rounded-xl shadow-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="text-xs uppercase bg-2/50 text-s-text border-b border-line">
                            <tr>
                                <th scope="col" className="px-6 py-4 font-bold tracking-wider">Role Name</th>
                                <th scope="col" className="px-6 py-4 font-bold tracking-wider hidden md:table-cell">Description</th>
                                <th scope="col" className="px-6 py-4 font-bold tracking-wider text-center w-32">Permissions</th>
                                <th scope="col" className="px-6 py-4 font-bold tracking-wider text-center w-32">Users</th>
                                <th scope="col" className="px-6 py-4 font-bold tracking-wider text-center w-32">Type</th>
                                <th scope="col" className="px-6 py-4 font-bold tracking-wider text-right w-24">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-line">
                            {isLoading ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-8 text-center text-s-text">
                                        <div className="animate-pulse flex flex-col items-center gap-3">
                                            <div className="w-8 h-8 border-4 border-[#5652CF] border-t-transparent rounded-full animate-spin"></div>
                                            Loading roles...
                                        </div>
                                    </td>
                                </tr>
                            ) : roles.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-8 text-center text-s-text">
                                        No roles found.
                                    </td>
                                </tr>
                            ) : (
                                roles.map((role) => (
                                    <tr
                                        key={role.id || role._id}
                                        onClick={() => handleView(role)}
                                        className="hover:bg-2/30 transition-colors group cursor-pointer"
                                    >
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="font-bold text-p-text">{role.name}</div>
                                        </td>
                                        <td className="px-6 py-4 text-s-text hidden md:table-cell">
                                            {role.description || <span className="italic opacity-50">No description</span>}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className="inline-flex items-center justify-center px-2.5 py-1 text-xs font-bold bg-[#5652CF]/10 text-blue-400 rounded-full">
                                                {role.permissions?.length || 0}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className="inline-flex items-center justify-center gap-1 px-2.5 py-1 text-xs font-bold bg-emerald-500/10 text-emerald-400 rounded-full">
                                                <IoPeopleOutline className="text-sm" />
                                                {role.user_count ?? 0}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            {role.is_system_role ? (
                                                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-purple-500/10 text-purple-400 rounded ring-1 ring-inset ring-purple-500/20">
                                                    System
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-gray-500/10 text-gray-400 rounded ring-1 ring-inset ring-gray-500/20">
                                                    Custom
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right">
                                            <div className="flex items-center justify-end gap-2 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleView(role); }}
                                                    className="p-1.5 text-s-text hover:text-blue-400 bg-2/50 hover:bg-[#5652CF]/10 rounded-md transition-colors"
                                                    title="View Role"
                                                >
                                                    <IoEyeOutline className="text-lg" />
                                                </button>
                                                {!role.is_system_role && (
                                                    <button
                                                        onClick={(e) => handleEdit(role, e)}
                                                        className="p-1.5 text-s-text hover:text-blue-400 bg-2/50 hover:bg-[#5652CF]/10 rounded-md transition-colors"
                                                        title="Edit Role"
                                                    >
                                                        <IoPencilOutline className="text-lg" />
                                                    </button>
                                                )}
                                                {!role.is_system_role && (
                                                    <button
                                                        onClick={(e) => handleDelete(role, e)}
                                                        className="p-1.5 text-s-text hover:text-red-400 bg-2/50 hover:bg-red-500/10 rounded-md transition-colors"
                                                        title="Delete Role"
                                                    >
                                                        <IoTrashOutline className="text-lg" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <RoleModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                roleToEdit={roleToEdit}
                mode={modalMode}
                onSave={loadRoles}
                userIdToken={userIdToken}
            />
        </div>
    );
};
